import { DOCUMENT } from '@angular/common';
import type { AfterViewInit, OnDestroy } from '@angular/core';
import { Component, Inject } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import type { PageDataResponse } from '@core-mkt/resolvers/page-data/page-data-response';
import type { Banner } from '@core-mkt/shared/components/banner/banner/banner';
import { AccessibilityService } from '@core-mkt/services/accessibility/accessibility.service';
import { CountdownService } from '@core-mkt/services/countdown/countdown.service';
import { EnvService } from '@core-mkt/services/env/env.service';
import { QueryParamDirective } from '@core-mkt/services/query-param/query-param.directive';
import type { EmailCapture } from '@core-mkt/shared/components/email-capture/email-capture/email-capture';
import { EmailCaptureComponent } from '@core-mkt/shared/components/email-capture/email-capture/email-capture.component';
import { PrivacyManagerComponent } from '@core-mkt/shared/components/privacy-manager/privacy-manager/privacy-manager.component';
import { cloneDeep } from 'lodash-es';
import { Subject } from 'rxjs';
import { FooterLinkComponent } from '../footer-link/footer-link.component';
import type { Footer, FooterLinkGroup, FooterLogo, ShareButtons } from './footer';
import { footerData } from './footer-data';

/**
 * Minimal shape of the craft `header` global fields this footer reads.
 * All optional — Craft may omit these globals, so reads use optional chaining.
 */
interface FooterHeaderData {
  emailCapture?: EmailCapture[];
  aaGraphicPromoBanner?: {
    showHideGraphicPromoBanner: boolean;
    startTime: number | null;
    endTime: number | null;
  }[];
  realEstateHeaderPromoBanner?: {
    showHidePromoBanner: boolean;
  }[];
}

@Component({
  selector: 'footer, aa-footer',
  templateUrl: './footer.component.html',
  styleUrls: ['./footer.component.scss'],
  standalone: true,
  imports: [EmailCaptureComponent, FooterLinkComponent, PrivacyManagerComponent, QueryParamDirective],
})
export class FooterComponent implements AfterViewInit, OnDestroy {
  pageData: Footer;
  emailCapture: EmailCapture;
  resolverData: PageDataResponse<FooterHeaderData>;
  displayRE = false;
  private readonly destroy$ = new Subject<void>();
  promoBannerActive = true;
  showStickyEmailCapture = true;
  graphicBanner: HTMLElement;
  showPrivacyManagerModal = false;
  private readonly copyrightYear_ = new Date().getFullYear();

  constructor(
    private route: ActivatedRoute,
    private countdownService: CountdownService,
    private accessibilityService: AccessibilityService,
    private env: EnvService,
    @Inject(DOCUMENT) private document: Document,
  ) {
    this.pageData = footerData;

    //Getting aria label to ShareButtons
    this.pageData.aceShareGroup.forEach((button) => {
      button.ariaLabel = this.accessibilityService.getAriaLabel(button.link);
    });

    const routePageData = this.route.snapshot.data['pageData'] as PageDataResponse<FooterHeaderData> | undefined;
    if (routePageData) {
      this.resolverData = cloneDeep(routePageData);
      if (this.resolverData.craftData.header) {
        this.emailCapture = this.resolverData.craftData.header?.emailCapture?.[0];
        const aaGraphicPromoBanner = this.resolverData.craftData.header?.aaGraphicPromoBanner?.[0];
        const realEstateHeaderPromoBanner = this.resolverData.craftData.header?.realEstateHeaderPromoBanner?.[0];
        this.promoBannerActive = realEstateHeaderPromoBanner?.showHidePromoBanner ?? true;
        if (
          aaGraphicPromoBanner &&
          aaGraphicPromoBanner.showHideGraphicPromoBanner &&
          realEstateHeaderPromoBanner?.showHidePromoBanner
        ) {
          const startTime = aaGraphicPromoBanner.startTime;
          const endTime = aaGraphicPromoBanner.endTime;

          if (startTime == null && endTime == null) {
            this.displayRE = true;
          } else if (startTime && endTime) {
            this.displayRE = this.countdownService.displayBanner(startTime, endTime);
          }
        }
      }
      // Craft delivers `banners` as an array of banner groups (each with a `topBanner: Banner[]`),
      // which is narrower than the shared `BannerData` object type; read it through that shape.
      const banners = this.resolverData.craftData.banners as unknown as { topBanner: Banner[] }[] | undefined;
      if (banners && banners[0]) {
        const promoData = banners[0].topBanner[0];
        if (promoData.showHidePromoBanner) {
          const startTime = promoData.promotionStartDateTime;
          const endTime = promoData.promotionEndsDateTime;

          if (startTime == null && endTime == null) {
            this.displayRE = true;
          } else if (startTime && endTime) {
            const startTimeStamp = new Date(startTime).getTime();
            const endTimeStamp = new Date(endTime).getTime();
            this.displayRE = this.countdownService.displayBanner(startTimeStamp, endTimeStamp);
          }
        }
      }
    }
  }

  ngAfterViewInit(): void {
    this.graphicBanner = this.document.getElementById('graphicBanner');
  }

  get copyrightYear(): number {
    return this.copyrightYear_;
  }

  get showHideFooter(): boolean {
    return !this.resolverData?.craftData?.headerData?.hideFooter;
  }

  get footerLogos(): FooterLogo[] {
    return this.pageData.aceFooterLogo;
  }

  get generalLinks(): FooterLinkGroup[] {
    return this.pageData.aceGeneralLinkGroup;
  }

  get generalLinks2(): FooterLinkGroup[] {
    return this.pageData.aceGeneralLinkGroup2;
  }

  get verticalLinks(): FooterLinkGroup[] {
    return this.pageData.aceVerticalLinkGroup;
  }

  get socialLinks(): FooterLinkGroup[] {
    return this.pageData.aceSocialLinkGroup;
  }

  get emailCaptureData(): EmailCapture {
    return this.emailCapture;
  }

  get shareButtons(): ShareButtons[] {
    return this.pageData.aceShareGroup;
  }

  itemLabel(button: ShareButtons): string {
    return button.label;
  }

  stripLink(rawLink: string): string {
    return rawLink.replace(/\w+:\/\//, '');
  }

  openPrivacyManagerModal() {
    this.showPrivacyManagerModal = true;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
